import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PiArrowLeftBold, PiCheckCircleBold, PiQrCodeBold } from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';

type PackageData = {
    client_label: string;
    order_ref: string;
    item_code: string | null;
    product_name: string | null;
    box_label?: string;
    package_index: number;
    package_total: number;
    container_number: string | null;
    invoice_number: string | null;
    box_weight_kg?: number | null;
    box_cbm_m3?: number | null;
    weight_tower_qty?: number | null;
    validated: boolean;
    conference_url: string;
};

export default function ConferencePackageScanPage() {
    const { id, shipmentId: shortShipmentId, checkItemId, packageIndex } = useParams();
    const shipmentId = Number(id ?? shortShipmentId);
    const itemId = Number(checkItemId);
    const pkg = Number(packageIndex);

    const [data, setData] = useState<PackageData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [scanned, setScanned] = useState(false);

    useEffect(() => {
        if (!shipmentId || !itemId || !pkg) return;

        let cancelled = false;

        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const showRes = await api.get<{ data: PackageData }>(
                    `/shipments/${shipmentId}/conference/packages/${itemId}/${pkg}`,
                );
                if (cancelled) return;
                setData(showRes.data.data);

                await api.post(`/shipments/${shipmentId}/conference/packages/${itemId}/${pkg}/scan`, {
                    validate: true,
                });
                if (!cancelled) {
                    setScanned(true);
                    setData((prev) => (prev ? { ...prev, validated: true } : prev));
                }
            } catch {
                if (!cancelled) {
                    setError('Não foi possível registrar esta etiqueta.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, [shipmentId, itemId, pkg]);

    return (
        <div className="mx-auto max-w-md p-4">
            <Link
                to={data?.conference_url ?? `/shipments/${shipmentId}/conference`}
                className="mb-4 inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
                <PiArrowLeftBold /> Voltar ao romaneio
            </Link>

            {loading ? <LoadingState /> : null}
            {error ? <p className="text-sm text-red-600">{error}</p> : null}

            {data && !loading ? (
                <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-4 py-3 text-white">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            <PiQrCodeBold className="size-5" />
                            Etiqueta rastreada
                        </div>
                        {scanned ? (
                            <p className="mt-1 flex items-center gap-1 text-xs text-emerald-100">
                                <PiCheckCircleBold /> Leitura registrada no embarque
                            </p>
                        ) : null}
                    </div>
                    <div className="space-y-2 p-4 text-sm text-gray-800">
                        <p>
                            <span className="text-gray-500">Container:</span>{' '}
                            <span className="font-mono font-semibold">{data.container_number ?? '—'}</span>
                        </p>
                        <p>
                            <span className="text-gray-500">Cliente:</span> {data.client_label}
                        </p>
                        <p className="font-mono text-lg font-bold">{data.item_code ?? '—'}</p>
                        <p className="text-gray-600">{data.product_name ?? '—'}</p>
                        <p className="inline-block rounded-md bg-gray-900 px-2 py-1 text-xs font-bold text-white">
                            Caixa {data.box_label ?? data.package_index} / {data.package_total}
                        </p>
                        <ul className="mt-3 grid grid-cols-3 gap-2 text-xs">
                            <li className="rounded-lg bg-orange-50 px-2 py-2 ring-1 ring-orange-100">
                                <span className="block text-gray-500">Cubagem</span>
                                <span className="font-semibold">
                                    {data.box_cbm_m3 != null
                                        ? `${data.box_cbm_m3.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³`
                                        : '—'}
                                </span>
                            </li>
                            <li className="rounded-lg bg-orange-50 px-2 py-2 ring-1 ring-orange-100">
                                <span className="block text-gray-500">Torres</span>
                                <span className="font-semibold">
                                    {data.weight_tower_qty != null && data.weight_tower_qty > 0
                                        ? data.weight_tower_qty
                                        : '—'}
                                </span>
                            </li>
                            <li className="rounded-lg bg-orange-50 px-2 py-2 ring-1 ring-orange-100">
                                <span className="block text-gray-500">Peso caixa</span>
                                <span className="font-semibold">
                                    {data.box_weight_kg != null
                                        ? `${data.box_weight_kg.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
                                        : '—'}
                                </span>
                            </li>
                        </ul>
                        {data.validated ? (
                            <p className="text-xs font-medium text-emerald-700">Caixa marcada como validada.</p>
                        ) : null}
                    </div>
                </article>
            ) : null}
        </div>
    );
}
